import { useTranslation } from 'react-i18next';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormItem, ModalNew, ModalNewProps, message } from '@helloworld1812/ws-components';

import IntlTelInput from '@/components/intl-tel-input';
import { useHrMutation } from '@/api';

interface ChangePhoneNumberModalProps extends ModalNewProps {
  regionNumber: string | null;
  phoneNumber: string | null;
  isRequired?: boolean;
  onCancel?: () => void;
  onChanged?: () => void;
}

const trimPhoneNumber = (phoneNumber: string | null) => {
  return phoneNumber?.replace(/[\s()-]/g, '') ?? '';
};

export const UserPhoneEditModal = (props: ChangePhoneNumberModalProps) => {
  const { t } = useTranslation('hr-account-routes');

  const { regionNumber, phoneNumber: phone, onCancel, onChanged, isRequired, ...restProps } = props;
  const {
    control,
    formState: { errors },
    setValue,
    setError,
    handleSubmit,
    clearErrors,
  } = useForm({
    defaultValues: {
      region_number: regionNumber,
      phone,
    },
  });

  const { mutate: updatePhoneNumber, isLoading } = useHrMutation(
    {
      url: '/v1/users/update_profile',
      method: 'put',
    },
    {
      onSuccess: () => {
        message.success(t('Phone number successfully updated.'));
        onChanged?.();
      },
      onError: () => {
        message.error(t('Failed to update phone number'));
      },
    }
  );

  const handleClickUpdate = async (values: { region_number: string; phone: string }) => {
    if (trimPhoneNumber(values.phone) === trimPhoneNumber(phone) && values.region_number === regionNumber) {
      setError('phone', { message: t('Please enter a different phone number') });
      return;
    }

    updatePhoneNumber({
      company_staff: values,
    });
  };

  const defaultValue = regionNumber && phone ? `+${regionNumber}${phone}` : undefined;

  return (
    <ModalNew
      variant="quick-action"
      headerBorder
      title={t('Edit Phone Number')}
      visible
      bodyStyle={{ padding: 0 }}
      onCancel={onCancel}
      onClose={onCancel}
      onSubmit={handleSubmit(handleClickUpdate)}
      okButtonProps={{
        loading: isLoading,
        'data-testid': 'change_phone_number_modal_update_button',
      }}
      contentProps={{ px: 40, pt: 2 }}
      asForm
      formId="change_phone_number_modal"
      {...restProps}
    >
      <FormItem name="phoneNumber" label={t('Phone Number')} error={errors.phone}>
        <Controller
          control={control}
          name="phone"
          render={() => (
            <IntlTelInput
              preferredCountries={['us', 'sg', 'ca']}
              style={{ width: 250 }}
              defaultValue={defaultValue}
              onPhoneNumberChange={(isValid, phoneNumber, countryData) => {
                if (isValid || !phoneNumber) {
                  setValue('phone', phoneNumber.replace(/\s/g, ''));
                  setValue('region_number', countryData.dialCode ?? '1');
                  clearErrors('phone');
                } else {
                  setError('phone', { message: t('Please enter a correct phone number') });
                }
              }}
            />
          )}
          rules={{
            required: {
              value: !!isRequired,
              message: t('Please enter phone number'),
            },
          }}
        />
      </FormItem>
    </ModalNew>
  );
};
